import { Link } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import { ArrowLeft, CalendarPlus, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Checkbox } from '@/components/ui/Checkbox'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Veld } from '@/components/ui/Veld'
import { Weeklegenda, Weekstrip } from '@/components/ui/Weekstrip'
import { formatUren } from '@/lib/format'
import {
  useGroep,
  useMaakLesmoment,
  useMaakLesreeks,
  useVerwijderLesmoment,
  useVerwijderLesreeks,
  useWijzigLesmoment,
  useZetWeek,
} from '@/lib/groep-api'
import type { Dag, Dagdeel, Lesmoment, LesmomentInvoer, Lesreeks, Week } from '@/lib/groep-types'
import {
  DAGDEEL_LABEL,
  DAGDEEL_VOLGORDE,
  DAG_LABEL,
  DAG_VOLGORDE,
  berekenLesreeksUren,
  berekenMentoraatUren,
  volgendeWeekstatus,
} from '@/lib/lesreeks'
import { useMedewerkers } from '@/lib/medewerker-api'
import { useProduct } from '@/lib/me-api'
import { useSemesterKeuze } from '@/lib/semester-context'
import { useSleutels } from '@/lib/semester-api'
import type { Sleutels } from '@/lib/semester-types'

const NIEMAND = 'niemand'
const HELE_REEKS = 'hele-reeks'

const DAG_OPTIES: SelectOption[] = DAG_VOLGORDE.map((dag) => ({ value: dag, label: DAG_LABEL[dag] }))

const DAGDEEL_OPTIES: SelectOption[] = DAGDEEL_VOLGORDE.map((dagdeel) => ({
  value: dagdeel,
  label: DAGDEEL_LABEL[dagdeel],
}))

export function GroepPage({ code }: { code: string }) {
  const { semesterId, semester, isLoading: semesterLaadt } = useSemesterKeuze()
  const query = useGroep(semesterId, code)
  const sleutels = useSleutels(semesterId)
  const medewerkers = useMedewerkers(true)
  const maakLesreeks = useMaakLesreeks(semesterId, code)
  const magBewerken = useProduct() === 'beheer'
  const [nieuweReeks, setNieuweReeks] = useState(false)

  const laadt = semesterLaadt || query.isLoading
  const groep = query.data

  const medewerkerOpties: SelectOption[] = [
    { value: NIEMAND, label: 'Nog te verdelen' },
    ...(medewerkers.data ?? []).map((medewerker) => ({
      value: medewerker.id,
      label: medewerker.naam,
    })),
  ]

  const totaalUren = (groep?.lesreeksen ?? []).reduce(
    (som, reeks) => som + (berekenLesreeksUren(reeks, sleutels) ?? 0),
    0,
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title={groep?.code ?? code}
        description={
          groep ? (
            <span>
              {[
                groep.trajectLabel,
                `${groep.aantalLesreeksen} ${groep.aantalLesreeksen === 1 ? 'lesreeks' : 'lesreeksen'}`,
                `${groep.leswekenInSemester} lesweken`,
                semester?.naam,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null
        }
        actions={
          <>
            {magBewerken ? (
              <button type="button" className="btn-secondary" onClick={() => setNieuweReeks(true)}>
                <Plus className="h-4 w-4" />
                Lesreeks toevoegen
              </button>
            ) : null}
            <Link to="/groepen" className="btn-ghost">
              <ArrowLeft className="h-4 w-4" />
              Terug naar groepen
            </Link>
          </>
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : laadt || !groep ? (
        <div className="space-y-4">
          <Skeleton className="h-[86px] w-full rounded-card" />
          <Skeleton className="h-72 w-full rounded-card" />
          <Skeleton className="h-72 w-full rounded-card" />
        </div>
      ) : (
        <>
          <Kerncijfers
            uren={totaalUren}
            lesweken={groep.leswekenInSemester}
            zonderDocent={groep.lesmomentenZonderMedewerker}
            sleutels={sleutels}
          />

          {groep.lesreeksen.length === 0 ? (
            <EmptyState
              icon={CalendarPlus}
              title="Nog geen lesreeksen"
              description="Een lesreeks is een vak met eigen lesweken en lesmomenten."
              action={
                magBewerken ? (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => setNieuweReeks(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Lesreeks toevoegen
                  </button>
                ) : null
              }
            />
          ) : (
            groep.lesreeksen.map((reeks) => (
              <LesreeksBlok
                key={reeks.id}
                semesterId={semesterId}
                code={code}
                reeks={reeks}
                sleutels={sleutels}
                medewerkerOpties={medewerkerOpties}
                magBewerken={magBewerken}
              />
            ))
          )}
        </>
      )}

      <NieuweLesreeksModal
        open={nieuweReeks}
        onOpenChange={setNieuweReeks}
        bezig={maakLesreeks.isPending}
        onBewaar={(vak) => maakLesreeks.mutate(vak, { onSuccess: () => setNieuweReeks(false) })}
      />
    </div>
  )
}

interface KerncijfersProps {
  uren: number
  lesweken: number
  zonderDocent: number
  sleutels: Sleutels | null
}

function Kerncijfers({ uren, lesweken, zonderDocent, sleutels }: KerncijfersProps) {
  const cellen = [
    {
      label: 'Lesuren in dit semester',
      waarde: sleutels ? `${formatUren(uren)} u` : '-',
      kleur: 'text-ink',
    },
    { label: 'Lesweken', waarde: String(lesweken), kleur: 'text-ink' },
    {
      label: 'Zonder docent',
      waarde: String(zonderDocent),
      kleur: zonderDocent > 0 ? 'text-status-onderbezet' : 'text-status-in-orde',
    },
  ]

  return (
    <div className="grid grid-cols-1 divide-y divide-hairline rounded-card bg-surface shadow-card sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {cellen.map((cel) => (
        <div key={cel.label} className="px-5 py-4">
          <p className="label-caps">{cel.label}</p>
          <p
            className={`num mt-1 text-left text-[22px] font-semibold tracking-[-0.02em] ${cel.kleur}`}
          >
            {cel.waarde}
          </p>
        </div>
      ))}
    </div>
  )
}

interface LesreeksBlokProps {
  semesterId: string
  code: string
  reeks: Lesreeks
  sleutels: Sleutels | null
  medewerkerOpties: SelectOption[]
  magBewerken: boolean
}

function LesreeksBlok({
  semesterId,
  code,
  reeks,
  sleutels,
  medewerkerOpties,
  magBewerken,
}: LesreeksBlokProps) {
  const zetWeek = useZetWeek(semesterId, code)
  const maakLesmoment = useMaakLesmoment(semesterId, code)
  const wijzigLesmoment = useWijzigLesmoment(semesterId, code)
  const verwijderLesmoment = useVerwijderLesmoment(semesterId, code)
  const verwijderLesreeks = useVerwijderLesreeks(semesterId, code)

  const uren = berekenLesreeksUren(reeks, sleutels)
  const mentoraat = berekenMentoraatUren(reeks, sleutels) ?? 0
  const weekOpties = weekkeuzes(reeks.weken)

  function wisselWeek(maandag: string, inhaal: boolean) {
    const week = reeks.weken.find((item) => item.maandag === maandag)
    if (!week) return
    const status = volgendeWeekstatus(week.status, inhaal)
    if (!status) return
    zetWeek.mutate({ lesreeksId: reeks.id, maandag, status })
  }

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-hairline px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{reeks.vak}</h2>
          <p className="mt-0.5 text-xs text-ink-faint">
            {[
              `${reeks.leswekenInSemester} lesweken in dit semester`,
              `${reeks.lesmomenten.length} ${reeks.lesmomenten.length === 1 ? 'lesmoment' : 'lesmomenten'}`,
              mentoraat > 0 ? `${formatUren(mentoraat)} u mentoraat` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="num text-sm font-semibold text-ink">
            {uren === null ? '-' : `${formatUren(uren)} u`}
          </span>
          {magBewerken ? (
            <button
              type="button"
              className="btn-ghost h-8 w-8 p-0"
              aria-label={`${reeks.vak} verwijderen`}
              onClick={() => verwijderLesreeks.mutate(reeks.id)}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.85} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="px-5 py-4">
        {reeks.weken.length === 0 ? (
          <p className="text-sm text-ink-faint">Deze reeks heeft nog geen weken.</p>
        ) : (
          <>
            <Weekstrip weken={reeks.weken} onToggle={magBewerken ? wisselWeek : undefined} />
            <Weeklegenda className="mt-4" />
            {magBewerken ? (
              <p className="mt-2 text-xs text-ink-faint">
                Klik op een week om les en geen les te wisselen. Shift-klik zet er een inhaalweek
                van. Vakantieweken liggen vast.
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="border-t border-hairline">
        <div className="hidden grid-cols-[6rem_6rem_minmax(0,1fr)_4rem_6rem_8rem_8rem_2rem] gap-3 px-5 py-2.5 lg:grid">
          <span className="label-caps">Dag</span>
          <span className="label-caps">Dagdeel</span>
          <span className="label-caps">Medewerker</span>
          <span className="label-caps">Mentor</span>
          <span className="label-caps">Lokaal</span>
          <span className="label-caps">Vanaf week</span>
          <span className="label-caps">Tot week</span>
          <span />
        </div>

        {reeks.lesmomenten.length === 0 ? (
          <p className="px-5 pb-4 text-sm text-ink-faint">
            Nog geen lesmomenten. Zonder lesmoment levert deze reeks geen uren op.
          </p>
        ) : (
          <ul className="divide-y divide-hairline border-t border-hairline">
            {reeks.lesmomenten.map((lesmoment) => (
              <LesmomentRegel
                key={lesmoment.id}
                lesmoment={lesmoment}
                weekOpties={weekOpties}
                medewerkerOpties={medewerkerOpties}
                magBewerken={magBewerken}
                onWijzig={(input) => wijzigLesmoment.mutate({ lesmomentId: lesmoment.id, input })}
                onVerwijder={() => verwijderLesmoment.mutate(lesmoment.id)}
              />
            ))}
          </ul>
        )}

        {magBewerken ? (
          <div className="border-t border-hairline px-5 py-3">
            <button
              type="button"
              className="btn-secondary"
              disabled={maakLesmoment.isPending}
              onClick={() =>
                maakLesmoment.mutate({
                  lesreeksId: reeks.id,
                  input: vrijLesmoment(reeks.lesmomenten),
                })
              }
            >
              <Plus className="h-4 w-4" />
              Lesmoment toevoegen
            </button>
          </div>
        ) : null}
      </div>
    </section>
  )
}

interface LesmomentRegelProps {
  lesmoment: Lesmoment
  weekOpties: SelectOption[]
  medewerkerOpties: SelectOption[]
  magBewerken: boolean
  onWijzig: (input: Partial<LesmomentInvoer>) => void
  onVerwijder: () => void
}

function LesmomentRegel({
  lesmoment,
  weekOpties,
  medewerkerOpties,
  magBewerken,
  onWijzig,
  onVerwijder,
}: LesmomentRegelProps) {
  const [lokaal, setLokaal] = useState(lesmoment.lokaal ?? '')

  if (!magBewerken) {
    return (
      <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-3 text-sm">
        <span className="text-ink">
          {lesmoment.dagLabel} {lesmoment.dagdeel}
        </span>
        <span className={lesmoment.medewerkerNaam ? 'text-ink-soft' : 'text-status-onderbezet'}>
          {lesmoment.medewerkerNaam ?? 'Nog te verdelen'}
        </span>
        {lesmoment.isMentor ? <span className="text-xs text-ink-faint">Mentor</span> : null}
        {lesmoment.lokaal ? (
          <span className="text-xs text-ink-faint">lokaal {lesmoment.lokaal}</span>
        ) : null}
        {lesmoment.vanafMaandag || lesmoment.totMaandag ? (
          <span className="text-xs text-ink-faint">
            {periodeTekst(lesmoment.vanafMaandag, lesmoment.totMaandag)}
          </span>
        ) : null}
      </li>
    )
  }

  return (
    <li className="grid grid-cols-2 gap-3 px-5 py-3 lg:grid-cols-[6rem_6rem_minmax(0,1fr)_4rem_6rem_8rem_8rem_2rem] lg:items-center">
      <Select
        value={lesmoment.dag}
        onValueChange={(waarde) => onWijzig({ dag: waarde as Dag })}
        options={DAG_OPTIES}
        className="w-full"
      />
      <Select
        value={lesmoment.dagdeel}
        onValueChange={(waarde) => onWijzig({ dagdeel: waarde as Dagdeel })}
        options={DAGDEEL_OPTIES}
        className="w-full"
      />
      <Select
        value={lesmoment.medewerkerId ?? NIEMAND}
        onValueChange={(waarde) =>
          onWijzig({ medewerkerId: waarde === NIEMAND ? null : waarde })
        }
        options={medewerkerOpties}
        className="col-span-2 w-full lg:col-span-1"
        searchPlaceholder="Zoek medewerker"
      />

      <Checkbox
        checked={lesmoment.isMentor}
        onChange={(aan) => onWijzig({ isMentor: aan })}
        label="Mentor"
        labelVerborgenOpDesktop
      />

      <input
        className="field h-9"
        value={lokaal}
        placeholder="Lokaal"
        onChange={(gebeurtenis) => setLokaal(gebeurtenis.target.value)}
        onBlur={() => {
          const schoon = lokaal.trim()
          if (schoon !== (lesmoment.lokaal ?? '')) onWijzig({ lokaal: schoon === '' ? null : schoon })
        }}
      />

      <Select
        value={lesmoment.vanafMaandag ?? HELE_REEKS}
        onValueChange={(waarde) =>
          onWijzig({ vanafMaandag: waarde === HELE_REEKS ? null : waarde })
        }
        options={[{ value: HELE_REEKS, label: 'Vanaf begin' }, ...weekOpties]}
        className="w-full"
        searchPlaceholder="Zoek week"
      />
      <Select
        value={lesmoment.totMaandag ?? HELE_REEKS}
        onValueChange={(waarde) => onWijzig({ totMaandag: waarde === HELE_REEKS ? null : waarde })}
        options={[{ value: HELE_REEKS, label: 'Tot eind' }, ...weekOpties]}
        className="w-full"
        searchPlaceholder="Zoek week"
      />

      <button
        type="button"
        className="btn-ghost h-8 w-8 justify-self-end p-0"
        aria-label="Lesmoment verwijderen"
        onClick={onVerwijder}
      >
        <Trash2 className="h-4 w-4" strokeWidth={1.85} />
      </button>
    </li>
  )
}

interface NieuweLesreeksModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bezig: boolean
  onBewaar: (vak: string) => void
}

function NieuweLesreeksModal({ open, onOpenChange, bezig, onBewaar }: NieuweLesreeksModalProps) {
  const [vak, setVak] = useState('')

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (vak.trim() === '') return
    onBewaar(vak.trim())
  }

  return (
    <Modal
      open={open}
      onOpenChange={(volgende) => {
        onOpenChange(volgende)
        if (!volgende) setVak('')
      }}
      title="Lesreeks toevoegen"
      description="Een lesreeks is een vak met eigen lesweken en lesmomenten."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuwe-lesreeks" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuwe-lesreeks" onSubmit={verstuur}>
        <Veld label="Vak">
          <input
            className="field"
            value={vak}
            onChange={(gebeurtenis) => setVak(gebeurtenis.target.value)}
            placeholder="Nederlands"
            required
          />
        </Veld>
      </form>
    </Modal>
  )
}

function weekkeuzes(weken: Week[]): SelectOption[] {
  return weken
    .filter((week) => week.inVenster)
    .map((week) => ({
      value: week.maandag,
      label: `wk ${week.weeknummer} · ${format(parseISO(week.maandag), 'd MMM', { locale: nl })}`,
    }))
}

function periodeTekst(vanaf: string | null, tot: string | null): string {
  const van = vanaf ? format(parseISO(vanaf), 'd MMM', { locale: nl }) : 'begin'
  const eind = tot ? format(parseISO(tot), 'd MMM', { locale: nl }) : 'eind'
  return `${van} tot ${eind}`
}

function vrijLesmoment(lesmomenten: Lesmoment[]): { dag: Dag; dagdeel: Dagdeel } {
  for (const dag of DAG_VOLGORDE) {
    for (const dagdeel of DAGDEEL_VOLGORDE) {
      const bezet = lesmomenten.some(
        (lesmoment) => lesmoment.dag === dag && lesmoment.dagdeel === dagdeel,
      )
      if (!bezet) return { dag, dagdeel }
    }
  }
  return { dag: 'ma', dagdeel: 'ochtend' }
}
